import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DotsThree, ExportIcon, FileIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { parseSize } from "@/zapi-frontend/shared/lib/parseSize";
import { exportProject, type TExportProjectOptions } from "@/zapi-frontend/widgets/Header/lib/pdf-export";
import { ExportOptionsModal } from "@/zapi-frontend/widgets/Header/ui/_ExportOptionsModal";
import { useExportHistory, type TExportHistoryItem } from "../ProjectOverviewIsland/hooks/useExportHistory";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import {
  ContextEmpty,
  ContextPage,
  ContextPageHeader,
  FILE_ROW_ACTION,
  FileList,
  FileRow
} from "../ProjectLayout/ui/ContextPage";

const FILE_TYPE_LABEL: Record<TExportHistoryItem["fileType"], string> = {
  pdf: "PDF",
  docx: "DOCX",
  md: "MD",
  pptx: "PPTX",
  presentation_pdf: "PDF"
};

function formatExportDate(value: string, lng: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(lng === "ja" ? "ja-JP" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function ExportRow({ item }: { item: TExportHistoryItem }) {
  const { t, i18n } = useTranslation(["projectHome", "common"]);
  const isDeck = ["pptx", "presentation_pdf"].includes(item.fileType);
  const size = item.fileSize ? parseSize(item.fileSize) : null;
  const author = [item.user.firstName, item.user.lastName].filter(Boolean).join(" ");
  const exported = formatExportDate(item.createdAt, i18n.language);
  const exportedLine =
    exported && author
      ? t("exports.exportedOnBy", { date: exported, author })
      : exported
      ? t("exports.exportedOn", { date: exported })
      : author
      ? t("exports.exportedBy", { author })
      : null;
  const provenance = [
    isDeck ? t("exports.deck") : t("exports.memo"),
    size ? `${size.value}${size.unit}` : null,
    exportedLine
  ]
    .filter(Boolean)
    .join(" · ");

  const handleDownload = async () => {
    try {
      const response = await fetch(item.fileUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(t("exports.downloadFailed"));
    }
  };

  return (
    <FileRow
      glyph={FileIcon}
      title={item.fileName}
      meta={
        <span className="truncate" title={provenance}>
          {provenance}
        </span>
      }
      format={FILE_TYPE_LABEL[item.fileType]}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("exports.rowActions", { name: item.fileName })}
                data-testid={`export-row-${item.id}-menu-trigger`}
                className={FILE_ROW_ACTION}
              />
            }
          >
            <DotsThree weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
            <DropdownMenuItem
              data-testid={`export-row-${item.id}-open-item`}
              onClick={() => window.open(item.fileUrl, "_blank")}
            >
              {t("exports.open")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`export-row-${item.id}-download-item`} onClick={handleDownload}>
              {t("exports.download")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
      testId={`export-row-${item.id}`}
    />
  );
}

export function ExportsIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const queryClient = useQueryClient();
  const { selectedProject } = useProjectStore();
  const { flushSave } = useEditorStore();

  const { data, isLoading } = useExportHistory(projectId, 100);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (options: TExportProjectOptions) => {
    setIsModalOpen(false);
    setIsExporting(true);
    const toastId = toast.loading(t("common:exporting"));
    try {
      await flushSave?.();
      await exportProject(selectedProject?.id as unknown as number, options);
      await queryClient.invalidateQueries({ queryKey: ["exportHistory", projectId] });
      toast.success(t("header.exportDocSuccess"), { id: toastId });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(t("header.exportDocFailed"), { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  const exports = data?.exports ?? [];

  return (
    <ContextPage testId="exports-island">
      <ContextPageHeader
        title={t("sidebar.exports")}
        actions={
          <Button
            type="button"
            className="rounded-full"
            onClick={() => setIsModalOpen(true)}
            disabled={isExporting}
            data-testid="exports-page-export"
          >
            {isExporting ? (
              <>
                <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                {t("common:exporting")}
              </>
            ) : (
              <>
                <ExportIcon weight="bold" />
                {t("common:export")}
              </>
            )}
          </Button>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : exports.length === 0 ? (
        <ContextEmpty icon={ExportIcon} title={t("exports.empty")} description={t("exports.emptyDescription")} />
      ) : (
        <FileList>
          {exports.map((item) => (
            <ExportRow key={item.id} item={item} />
          ))}
        </FileList>
      )}
      <ExportOptionsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        projectName={selectedProject?.name || ""}
      />
    </ContextPage>
  );
}
